import { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { createPlayerScore, updatePlayerScore, getAwardCategories, assignPlayerAward } from '../../lib/supabase';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Score {
  id: string;
  player_id: string;
  tournament_id: string;
  round: number;
  score?: number | null;
  date: string;
  notes?: string;
  category?: string;
  player_status?: 'PLAYED' | 'DNP' | 'WD' | 'DQ';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  score: Score | null;
  playerId: string;
  tournamentId: string;
  onSuccess: () => void;
  existingRounds?: number[];
}

interface FormState {
  round: string;
  player_status: 'PLAYED' | 'DNP' | 'WD' | 'DQ';
  score: string;
  date: string;
  category: string;
  award_id: string;
  notes: string;
  award_notes: string;
}

const emptyForm = (playerId: string, tournamentId: string, playerCategory: string): FormState => ({
  round: '1',
  player_status: 'PLAYED',
  score: '',
  date: new Date().toISOString().split('T')[0],
  category: playerCategory,
  award_id: '',
  notes: '',
  award_notes: '',
});

export default function MobilePlayerScoreSheet({
  isOpen,
  onClose,
  score,
  playerId,
  tournamentId,
  onSuccess,
  existingRounds = [],
}: Props) {
  const [form, setForm] = useState<FormState>(emptyForm(playerId, tournamentId, ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [playerCategory, setPlayerCategory] = useState('');
  const [awardCategories, setAwardCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [playerAwards, setPlayerAwards] = useState<Array<{ id: string; award_id: string; notes: string }>>([]);

  useEffect(() => {
    if (isOpen && playerId) {
      fetchPlayerCategory();
      fetchAwardCategories();
      fetchPlayerAwards();
    }
  }, [isOpen, playerId]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (score) {
        setForm({
          round: String(score.round),
          player_status: score.player_status || 'PLAYED',
          score: score.score != null ? String(score.score) : '',
          date: score.date,
          category: score.category || playerCategory,
          award_id: '',
          notes: score.notes || '',
          award_notes: '',
        });
      } else {
        setForm(emptyForm(playerId, tournamentId, playerCategory));
      }
    }
  }, [isOpen, score, playerCategory]);

  const fetchPlayerCategory = async () => {
    try {
      const { data } = await supabase.from('players').select('category').eq('id', playerId).maybeSingle();
      if (data) {
        setPlayerCategory(data.category);
        setForm(prev => ({ ...prev, category: data.category }));
      }
    } catch {}
  };

  const fetchAwardCategories = async () => {
    try {
      const { data } = await getAwardCategories();
      if (data) setAwardCategories(data);
    } catch {}
  };

  const fetchPlayerAwards = async () => {
    try {
      const { data } = await supabase
        .from('player_awards')
        .select('*')
        .eq('player_id', playerId)
        .eq('tournament_id', tournamentId);
      if (data) setPlayerAwards(data);
    } catch {}
  };

  const f = (key: keyof FormState, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.player_status === 'PLAYED' && !form.score) {
      setError('Score is required when status is Played');
      return;
    }

    try {
      setSaving(true);

      const scoreData = {
        player_id: playerId,
        tournament_id: tournamentId,
        round: parseInt(form.round),
        score: form.player_status === 'PLAYED' ? parseInt(form.score) : null,
        date: form.date,
        notes: form.notes || undefined,
        player_status: form.player_status,
      };

      if (score) {
        const { error: err } = await updatePlayerScore(score.id, scoreData);
        if (err) throw err;
        toast.success('Score updated');
      } else {
        const { error: err } = await createPlayerScore(scoreData);
        if (err) throw err;
        toast.success('Score added');
      }

      if (form.category !== playerCategory) {
        await supabase.from('players').update({ category: form.category }).eq('id', playerId);
      }

      await assignPlayerAward({
        player_id: playerId,
        tournament_id: tournamentId,
        award_id: form.award_id || undefined,
        notes: form.award_notes || '',
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save score');
    } finally {
      setSaving(false);
    }
  };

  const removeAward = async (awardId: string) => {
    try {
      const { error } = await supabase.from('player_awards').delete().eq('id', awardId);
      if (error) throw error;
      fetchPlayerAwards();
      toast.success('Award removed');
    } catch {
      toast.error('Failed to remove award');
    }
  };

  if (!isOpen) return null;

  const field = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50';
  const label = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">{score ? 'Edit Score' : 'Add Score'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Enter player score details</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Round *</label>
              <select
                value={form.round}
                onChange={e => f('round', e.target.value)}
                disabled={!!score}
                className={`${field} ${score ? 'bg-gray-100 opacity-60' : ''}`}
              >
                {[1, 2, 3, 4].map(r => (
                  <option key={r} value={r} disabled={!score && existingRounds.includes(r)}>
                    Round {r}{!score && existingRounds.includes(r) ? ' (recorded)' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Status *</label>
              <select
                value={form.player_status}
                onChange={e => {
                  const s = e.target.value as 'PLAYED' | 'DNP' | 'WD' | 'DQ';
                  setForm(prev => ({ ...prev, player_status: s, score: s !== 'PLAYED' ? '' : prev.score }));
                }}
                className={field}
              >
                <option value="PLAYED">Played</option>
                <option value="DNP">Did Not Play</option>
                <option value="WD">Withdrawn</option>
                <option value="DQ">Disqualified</option>
              </select>
            </div>
          </div>

          {form.player_status === 'PLAYED' ? (
            <div>
              <label className={label}>Score *</label>
              <input
                type="number"
                value={form.score}
                onChange={e => f('score', e.target.value)}
                placeholder="e.g. -2, 0, +3"
                className={field}
              />
              <p className="text-xs text-gray-400 mt-1">Relative to par (negative = under, positive = over)</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-sm text-amber-700">
                {form.player_status === 'DNP' && 'Player did not participate in this round.'}
                {form.player_status === 'WD' && 'Player withdrew from this round.'}
                {form.player_status === 'DQ' && 'Player was disqualified from this round.'}
              </p>
            </div>
          )}

          <div>
            <label className={label}>Date *</label>
            <input
              type="date"
              value={form.date}
              onChange={e => f('date', e.target.value)}
              className={field}
            />
          </div>

          <div>
            <label className={label}>Player Category *</label>
            <select value={form.category} onChange={e => f('category', e.target.value)} className={field}>
              <option value="">Select category</option>
              <option value="Professional">Professional</option>
              <option value="Amateur">Amateur</option>
              <option value="Senior">Senior</option>
              <option value="Junior">Junior</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Changing this will update the player's category</p>
          </div>

          <div>
            <label className={label}>Award Category</label>
            <select value={form.award_id} onChange={e => f('award_id', e.target.value)} className={field}>
              <option value="">No Award</option>
              {awardCategories.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          {form.award_id && (
            <div>
              <label className={label}>Award Notes</label>
              <textarea
                value={form.award_notes}
                onChange={e => f('award_notes', e.target.value)}
                rows={2}
                placeholder="Optional award notes"
                className={`${field} resize-none`}
              />
            </div>
          )}

          <div>
            <label className={label}>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => f('notes', e.target.value)}
              rows={3}
              placeholder="Additional notes about this round"
              className={`${field} resize-none`}
            />
          </div>

          {playerAwards.length > 0 && (
            <div>
              <label className={label}>Current Awards</label>
              <div className="space-y-2">
                {playerAwards.map(award => (
                  <div key={award.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-golf-gray">
                        {awardCategories.find(a => a.id === award.award_id)?.name || 'Unknown Award'}
                      </p>
                      {award.notes && <p className="text-xs text-gray-500 mt-0.5">{award.notes}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAward(award.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pb-2" />
        </form>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={handleSave} disabled={saving} className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors">
            {saving ? 'Saving...' : score ? 'Update Score' : 'Add Score'}
          </button>
        </div>
      </div>
    </div>
  );
}
